/**
 * Document List Section — Shows documents associated with a subject.
 *
 * Displays documents grouped by status (pending signature, signed, archived).
 * Uses the Document primitive queries from document_api.
 *
 * CONSTRAINTS:
 * - DRR-055: Dumb frontend — document types from backend
 * - DRR-081: Document upload format restriction (PDF, TXT, CSV, MD only)
 */

import { useMemo } from 'react';
import {
  Card,
  Group,
  Stack,
  Text,
  Badge,
  Skeleton,
  Alert,
  ThemeIcon,
  SimpleGrid,
  Paper,
} from '@mantine/core';
import {
  IconFileText,
  IconFilePlus,
  IconFileCheck,
  IconFileX,
  IconAlertCircle,
  IconSignature,
} from '@tabler/icons-react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import type { WidgetProps } from '@ferp/core';

// Query to get documents by subject (using constellation membership)
// TODO: Add a dedicated documentsBySubject resolver for efficiency
const GET_DOCUMENTS_BY_SUBJECT = gql`
  query GetDocumentsBySubject($status: String) {
    documentApiGetDocumentsByStatus(status: $status) {
      id
      name
      documentType
      status
      createdAt
      signatureStatus
      contentType
    }
  }
`;

interface Document {
  id: string;
  name: string;
  documentType?: string | null;
  status?: string | null;
  createdAt?: string | null;
  signatureStatus?: string | null;
  contentType?: string | null;
}

interface GetDocumentsBySubjectData {
  documentApiGetDocumentsByStatus: Document[];
}


interface DocumentStats {
  pending: number;
  signed: number;
  draft: number;
  total: number;
}

/**
 * Shows document counts grouped by status.
 */
function DocumentStatsCard({ stats }: { stats: DocumentStats }) {
  return (
    <SimpleGrid cols={4} spacing="xs">
      <Paper p="xs" withBorder>
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="yellow">
            <IconSignature size={12} />
          </ThemeIcon>
          <div>
            <Text fw={600} size="lg">
              {stats.pending}
            </Text>
            <Text size="xs" c="dimmed">
              Pending
            </Text>
          </div>
        </Group>
      </Paper>

      <Paper p="xs" withBorder>
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="green">
            <IconFileCheck size={12} />
          </ThemeIcon>
          <div>
            <Text fw={600} size="lg">
              {stats.signed}
            </Text>
            <Text size="xs" c="dimmed">
              Signed
            </Text>
          </div>
        </Group>
      </Paper>

      <Paper p="xs" withBorder>
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="blue">
            <IconFilePlus size={12} />
          </ThemeIcon>
          <div>
            <Text fw={600} size="lg">
              {stats.draft}
            </Text>
            <Text size="xs" c="dimmed">
              Draft
            </Text>
          </div>
        </Group>
      </Paper>

      <Paper p="xs" withBorder>
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="gray">
            <IconFileText size={12} />
          </ThemeIcon>
          <div>
            <Text fw={600} size="lg">
              {stats.total}
            </Text>
            <Text size="xs" c="dimmed">
              Total
            </Text>
          </div>
        </Group>
      </Paper>
    </SimpleGrid>
  );
}

/**
 * Displays documents associated with a subject.
 *
 * Currently shows document statistics. Full document list with
 * filtering will be added when documentsBySubject resolver is available.
 */
export function DocumentListSection({ subjectActorId }: WidgetProps) {
  // Query pending signature documents
  const { data: pendingData, loading: pendingLoading } = useQuery<GetDocumentsBySubjectData>(
    GET_DOCUMENTS_BY_SUBJECT,
    {
      variables: { status: 'PENDING_SIGNATURE' },
      fetchPolicy: 'cache-and-network',
    }
  );

  // Query signed documents
  const { data: signedData, loading: signedLoading } = useQuery<GetDocumentsBySubjectData>(
    GET_DOCUMENTS_BY_SUBJECT,
    {
      variables: { status: 'SIGNED' },
      fetchPolicy: 'cache-and-network',
    }
  );

  // Query draft documents
  const { data: draftData, loading: draftLoading } = useQuery<GetDocumentsBySubjectData>(
    GET_DOCUMENTS_BY_SUBJECT,
    {
      variables: { status: 'DRAFT' },
      fetchPolicy: 'cache-and-network',
    }
  );

  const loading = pendingLoading || signedLoading || draftLoading;

  // Calculate stats
  // TODO: Filter by subjectActorId when resolver supports it
  const stats: DocumentStats = useMemo(() => {
    const pending = pendingData?.documentApiGetDocumentsByStatus?.length ?? 0;
    const signed = signedData?.documentApiGetDocumentsByStatus?.length ?? 0;
    const draft = draftData?.documentApiGetDocumentsByStatus?.length ?? 0;
    return {
      pending,
      signed,
      draft,
      total: pending + signed + draft,
    };
  }, [pendingData, signedData, draftData]);

  // Recent pending documents (first 3)
  const recentPending = useMemo(() => {
    return (pendingData?.documentApiGetDocumentsByStatus ?? []).slice(0, 3);
  }, [pendingData]);

  if (loading && !pendingData && !signedData && !draftData) {
    return (
      <Stack gap="sm">
        <Skeleton height={80} />
        <Skeleton height={60} />
        <Skeleton height={60} />
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      {/* Document stats */}
      <DocumentStatsCard stats={stats} />

      {/* Pending signature alert */}
      {stats.pending > 0 && (
        <Alert
          icon={<IconSignature size={16} />}
          color="yellow"
          variant="light"
          title={`${stats.pending} document${stats.pending !== 1 ? 's' : ''} pending signature`}
        >
          <Stack gap="xs">
            {recentPending.map((doc) => (
              <Group key={doc.id} gap="xs">
                <IconFileText size={14} />
                <Text size="xs">{doc.name}</Text>
                <Badge size="xs" variant="outline">
                  {doc.documentType ?? 'Document'}
                </Badge>
              </Group>
            ))}
            {stats.pending > 3 && (
              <Text size="xs" c="dimmed">
                +{stats.pending - 3} more
              </Text>
            )}
          </Stack>
        </Alert>
      )}

      {/* Empty state */}
      {stats.total === 0 && (
        <Alert icon={<IconFileText size={16} />} color="blue" variant="light">
          No documents associated with this subject yet.
        </Alert>
      )}

      {/* TODO: Add document list with filtering */}
      <Text size="xs" c="dimmed">
        Documents tab shows all documents linked to this subject via constellation membership.
      </Text>
    </Stack>
  );
}
